import axios from 'axios';
import Offcanvas from 'react-bootstrap/Offcanvas';
import OffCanvasBackBtn from 'components/offcanvas/OffCanvasBackBtn';
import OffCanvasCloseBtn from 'components/offcanvas/OffCanvasCloseBtn';
import AutocompleteAddress from 'components/autocomplete/AutocompleteAddress';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import FormCheck from 'react-bootstrap/FormCheck';
import Spinner from 'react-bootstrap/Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoCloseCircle, IoTrash } from 'react-icons/io5';
import moment from 'moment';
import { toast } from 'react-toastify';
import { dispatchAppEvent } from 'utils/helpers';
import { selectProducts } from 'store/selectors';


const PRODUCT_TYPE_LABELS = {
    PRIMARY: 'Principali',
    EXTRA: 'Extra',
    GENERIC: 'Personal brand'
};

// Il servizio con consegna rapida e' storicamente il prodotto 9, ma il flag
// viene letto dal prodotto stesso (priority_active / priority_price) quando disponibile.
const FAST_DELIVERY_PRODUCT_ID = 9;
const DEFAULT_FAST_DELIVERY_PRICE = 25;

const DEFAULT_SERVICE_ROW = {
    productId: '',
    price: '0,00',
    fastDelivery: false
};

const DEFAULT_FORM_DATA = {
    date: '',
    start_time: '',
    end_time: ''
};

const DEFAULT_TRAVEL_ADDRESS_OBJECT = {
    address: {
        full: 'Via Marcantonio Parenti 47, Modena, 41122, Modena (MO)',
        locality: 'Modena',
        postal_code: '41122',
        state: 'Modena',
        state_code: 'MO',
        street: 'Via Marcantonio Parenti 47'
    },
    coords: {
        lat: 44.659993,
        lng: 10.921706
    }
};

function parseCurrencyInput(value) {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

    const normalizedValue = `${value}`
        .trim()
        .replace(/\s/g, '')
        .replace(/\./g, '')
        .replace(',', '.');

    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function formatCurrencyValue(value) {
    return (Number.isFinite(value) ? value : 0).toFixed(2).replace('.', ',');
}

function supportsFastDelivery(product) {
    if (!product) return false;
    return !!product.priority_active || Number(product.id) === FAST_DELIVERY_PRODUCT_ID;
}

function getFastDeliveryPrice(product) {
    if (!supportsFastDelivery(product)) return 0;
    const priorityPrice = parseCurrencyInput(product.priority_price);
    return priorityPrice > 0 ? priorityPrice : DEFAULT_FAST_DELIVERY_PRICE;
}


function StaticField({ label, children }) {
    return (
        <div className="form-group boxed custom">
            <div className="input-wrapper">
                <Form.Label className="label-bordered">{label}</Form.Label>
                {children}
            </div>
        </div>
    );
}


function ToggleField({ id, label, checked = false, onChange = () => {} }) {
    return (
        <ul className="listview simple-listview no-border mt-1 mb-2 rounded form-group">
            <li className="form-control text-white">
                <div className='lh-1'>{label}</div>
                <FormCheck
                    id={id}
                    type="switch"
                    label=""
                    checked={checked}
                    onChange={onChange}
                />
            </li>
        </ul>
    );
}

export default function OffCanvasAddServiceAppointment({
    show,
    handleClose,
    handleBack,
    organization,
    resourceInfo,
    agents,
    isLoadingAgents,
    products,
    isLoadingProducts,
    handleSelectServiceOrganization
}) {
    const endTimeInputRef = useRef(null);

    const [serviceRows, setServiceRows] = useState([{ ...DEFAULT_SERVICE_ROW }]);
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
    const [description, setDescription] = useState('');
    const [address, setAddress] = useState('');
    const [addressObj, setAddressObj] = useState();
    const [travelAddress, setTravelAddress] = useState('');
    const [travelAddressObj, setTravelAddressObj] = useState();
    const [travelCost, setTravelCost] = useState('0,00');
    const [isLoadingTravelCost, setIsLoadingTravelCost] = useState(false);
    const [discountDescription, setDiscountDescription] = useState('');
    const [discount, setDiscount] = useState('0,00');
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [note, setNote] = useState('');
    const [toggleValues, setToggleValues] = useState({
        onSite: false,
        travelEnabled: false,
        discountEnabled: false
    });
    const getProductById = useCallback(
        (productId) => (products || []).find(product => `${product.id}` === `${productId}`),
        [products]
    );

    const groupedProducts = ['PRIMARY', 'EXTRA', 'GENERIC'].map(type => ({
        type,
        label: PRODUCT_TYPE_LABELS[type],
        items: (products || []).filter(product => product.type === type)
    })).filter(group => group.items.length);

    const fastDeliveryTotal = useMemo(() => {
        return serviceRows.reduce((sum, row) => {
            if (!row.fastDelivery) return sum;
            return sum + getFastDeliveryPrice(getProductById(row.productId));
        }, 0);
    }, [serviceRows, getProductById]);

    const subtotal = useMemo(() => {
        const servicesTotal = serviceRows.reduce((sum, row) => sum + parseCurrencyInput(row.price), 0);
        const travelTotal = toggleValues.travelEnabled ? parseCurrencyInput(travelCost) : 0;
        return formatCurrencyValue(servicesTotal + fastDeliveryTotal + travelTotal);
    }, [serviceRows, fastDeliveryTotal, toggleValues.travelEnabled, travelCost]);

    const totalVatExcluded = useMemo(() => {
        const subtotalValue = parseCurrencyInput(subtotal);
        const discountValue = toggleValues.discountEnabled ? parseCurrencyInput(discount) : 0;
        return formatCurrencyValue(subtotalValue - discountValue);
    }, [subtotal, toggleValues.discountEnabled, discount]);

    useEffect(() => {
        if (!show) {
            setFormData(DEFAULT_FORM_DATA);
            setServiceRows([{ ...DEFAULT_SERVICE_ROW }]);
            setDescription('');
            setAddress('');
            setAddressObj();
            setTravelAddress('');
            setTravelAddressObj();
            setTravelCost('0,00');
            setIsLoadingTravelCost(false);
            setDiscountDescription('');
            setDiscount('0,00');
            setSelectedAgentId('');
            setNote('');
            setToggleValues({
                onSite: false,
                travelEnabled: false,
                discountEnabled: false
            });
            return;
        }

        setFormData({
            ...DEFAULT_FORM_DATA,
            date: resourceInfo?.date ? moment(resourceInfo.date).format('YYYY-MM-DD') : ''
        });
    }, [show, resourceInfo]);

    useEffect(() => {
        if (!endTimeInputRef.current) return;

        const hasInvalidTimeRange = formData.start_time && formData.end_time && formData.start_time >= formData.end_time;
        endTimeInputRef.current.setCustomValidity(
            hasInvalidTimeRange ? 'L\'ora di fine deve essere successiva all\'ora di inizio.' : ''
        );
    }, [formData.start_time, formData.end_time]);

    useEffect(() => {

        console.log('Calculating travel cost with', organization);
        
        if (!toggleValues.travelEnabled) {
            setTravelCost('0,00');
            setIsLoadingTravelCost(false);
            return;
        }

        const fromCoords = travelAddressObj?.coords;
        const toCoords = organization?.building?.coords ?? addressObj?.coords;

        if (!fromCoords || !toCoords) {
            setTravelCost('0,00');
            setIsLoadingTravelCost(false);
            return;
        }

        let isCancelled = false;
        setIsLoadingTravelCost(true);

        axios.get(process.env.REACT_APP_FIREBASE_FUNCTIONS_URL + 'calendar-calculatetolls', {
            params: {
                start_point: [fromCoords.lng, fromCoords.lat].join(','),
                end_point: [toCoords.lng, toCoords.lat].join(',')
            }
        })
        .then((res) => {
            if (isCancelled) return;
            const toll = (res.data.toll ?? 0) * 2;
            const kmRefund = ((Math.round(0.4 * (res.data.length ?? 0) / 1000, 2) ?? 0) * 2);
            setTravelCost(((+toll || 0) + (+kmRefund || 0)).toFixed(2).replace('.', ','));
            setIsLoadingTravelCost(false);
        })
        .catch(() => {
            if (isCancelled) return;
            setTravelCost('0,00');
            setIsLoadingTravelCost(false);
        });

        return () => {
            isCancelled = true;
        };
    }, [addressObj, travelAddressObj, toggleValues.travelEnabled, organization?.building]);

    const handleRetriveAddress = (autocompleteObj) => {
        setAddressObj({
            address: autocompleteObj.values,
            coords: autocompleteObj.coords
        });
    };

    const handleResetAddress = () => {
        setAddress('');
        setAddressObj();
    };

    const handleResetBuilding = () => {
        handleSelectServiceOrganization(organization, null);
    }

    const handleRetriveTravelAddress = (autocompleteObj) => {
        setTravelAddressObj({
            address: autocompleteObj.values,
            coords: autocompleteObj.coords
        });
    };

    const handleResetTravelAddress = () => {
        setTravelAddress('');
        setTravelAddressObj();
    };

    const handleChangeTravelCost = (event) => {
        setTravelCost(event.target.value);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        if (name === 'date' && !value) return;

        const updateObject = {
            ...formData,
            [name]: value,
        };

        if (name === 'start_time') {
            const [hour, minute] = value.split(':');
            if (hour !== undefined && minute !== undefined) {
                const newStart = moment(formData.date || undefined).hour(hour).minute(minute);
                updateObject.end_time = newStart.add(30, 'minutes').format('HH:mm');
            }
        }

        setFormData(updateObject);
    };

    const handleToggleChange = (toggleKey) => {
        if (toggleKey === 'travelEnabled' && toggleValues.travelEnabled) {
            handleResetTravelAddress();
            setTravelCost('0,00');
        }

        if (toggleKey === 'travelEnabled' && !toggleValues.travelEnabled) {
            setTravelAddress(DEFAULT_TRAVEL_ADDRESS_OBJECT.address.full);
            setTravelAddressObj(DEFAULT_TRAVEL_ADDRESS_OBJECT);
        }

        setToggleValues(currentValues => ({
            ...currentValues,
            [toggleKey]: !currentValues[toggleKey]
        }));
    };

    const handleChangeSelectedProduct = (rowIndex, event) => {
        const productId = event.target.value;
        setServiceRows(currentRows => currentRows.map((row, index) => {
            if (index !== rowIndex) return row;
            return {
                ...row,
                productId,
                price: DEFAULT_SERVICE_ROW.price,
                fastDelivery: false
            };
        }));
    };

    const handleChangeServicePrice = (rowIndex, event) => {
        const value = event.target.value;
        setServiceRows(currentRows => currentRows.map((row, index) => {
            if (index !== rowIndex) return row;
            return {
                ...row,
                price: value
            };
        }));
    };

    const handleToggleServiceFastDelivery = (rowIndex) => {
        setServiceRows(currentRows => currentRows.map((row, index) => {
            if (index !== rowIndex) return row;
            return {
                ...row,
                fastDelivery: !row.fastDelivery
            };
        }));
    };

    const handleAddServiceRow = () => {
        setServiceRows(currentRows => ([
            ...currentRows,
            { ...DEFAULT_SERVICE_ROW }
        ]));
    };

    const handleRemoveServiceRow = (rowIndex) => {
        setServiceRows(currentRows => currentRows.filter((row, index) => index !== rowIndex));
    }

    const handleConfirm = (event) => {
        event.preventDefault();

        const normalizedAgentId = selectedAgentId ? Number(selectedAgentId) : null;
        const appointment = {
                description: description || '',
                building_id: organization?.building_id ?? organization?.building?.id ?? null,
                agent_id: normalizedAgentId,
                photographer_id: resourceInfo?.resource?.id ?? null,
                note,
                coords: addressObj?.coords ?? null,
                address: addressObj?.address ?? null,
                estimated_start_at: formData.date && formData.start_time ? `${formData.date} ${formData.start_time}` : null,
                estimated_end_at: formData.date && formData.end_time ? `${formData.date} ${formData.end_time}` : null,
                agent_on_site: toggleValues.onSite
        };
        
        // Il backend calcola final_price = total - discount + travel_paid,
        // quindi la trasferta va passata a parte e non dentro "totale".
        const travelPaid = toggleValues.travelEnabled ? parseCurrencyInput(travelCost) : 0;

        const orders = {
                organization_id: organization?.id ?? null,
                agent_id: normalizedAgentId,
                status: 'awaiting_payment',
                payment_method: 'BACS',
                is_confirm_payed: false,
                totale: Math.round((parseCurrencyInput(subtotal) - travelPaid) * 100) / 100,
                travel_paid: travelPaid,
                discount: toggleValues.discountEnabled ? parseCurrencyInput(discount) : 0,
                discount_label: toggleValues.discountEnabled ? discountDescription : ''
        };  

        const orderProducts = serviceRows?.map(row => {
            const product = getProductById(row.productId);
            const hasFastDelivery = !!row.fastDelivery && supportsFastDelivery(product);

            return {
                id: row.productId,
                price: parseCurrencyInput(row.price),
                hasFastDelivery,
                priority_price: hasFastDelivery ? getFastDeliveryPrice(product) : 0
            };
        });


        axios.post('appointments/manual-schedule', {
                appointment: appointment,
                products: orderProducts,
                orders: orders
        }).then(res => {
            console.log('API payload', {
                appointment,
                products: orderProducts,
                orders,
                res
            });

            if (res.data.status === false) return toast.error(res.data.message);
            dispatchAppEvent('appointments_add', res.data);
            handleClose();
            toast.success('Appuntamento inserito con successo');

        }).catch(err => {
            console.error('API error', err);
            toast.error('Si è verificato un errore durante l\'inserimento');
        });
    };

    const isEndTimeError = formData.start_time && formData.end_time ? formData.start_time >= formData.end_time : false;

    return (
    <>
        <Offcanvas
            placement="bottom"
            className="action-sheet"
            show={!!organization && show}
            onHide={handleClose}
        >
        <OffCanvasBackBtn handleBack={handleBack} />
        <OffCanvasCloseBtn handleClose={handleClose} />
            <Offcanvas.Body>

                    <div className="section mt-3 text-center">
                        <h3 className='mb-0'>Aggiungi appuntamento</h3>
                        <div>{resourceInfo?.resource?.title}</div>
                    </div>
            <Form className="action-sheet-content py-2 px-2" onSubmit={handleConfirm}>
                    <StaticField label="Nome cliente">
                        <Form.Control
                            type="text"
                            defaultValue={organization?.display_name || ''}
                            disabled
                        />
                    </StaticField>

                    {organization?.building && (
                    <StaticField label="Immobile Selezionato">
                            <Form.Control
                            type="text"
                            defaultValue={organization.building.title || ''}
                            disabled
                            />
                            <IoCloseCircle className="input-icon-search cursor-pointer" onClick={handleResetBuilding}/>
                    </StaticField>
                    )}

               

                    <StaticField label="Descrizione appuntamento*">
                        <Form.Control
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            required={true}
                        />
                    </StaticField>

                    <div className="row">
                        <div className="col-6">
                            <StaticField label="Seleziona agente">
                                <Form.Select className="form-control" value={selectedAgentId} onChange={(event) => setSelectedAgentId(event.target.value)}>
                                    <option value="">
                                        {isLoadingAgents ? 'Caricamento agenti...' : 'Nessuno'}
                                    </option>
                                    {agents?.map(agent => (
                                        <option key={agent.pivot_id} value={agent.pivot_id}>
                                            {agent.display_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </StaticField>
                        </div>
                        <div className="col-6">
                            <ToggleField
                                id="service-on-site"
                                label="Sul posto"
                                checked={toggleValues.onSite}
                                onChange={() => handleToggleChange('onSite')}
                            />
                        </div>
                    </div>

                    <div className="form-group boxed custom">
                        <div className="input-wrapper">
                            <label className="form-label">Data inizio*</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date"
                                autoComplete="off"
                                value={formData.date}
                                onChange={handleChange}
                                required={true}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <div className="form-group boxed custom">
                                <div className="input-wrapper">
                                    <label className="label-bordered form-label">Ora inizio*</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        style={{ background: '#3a3a3a' }}
                                        name="start_time"
                                        value={formData.start_time}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group boxed custom">
                                <div className="input-wrapper">
                                    <label className="label-bordered form-label">Ora fine*</label>
                                    <input
                                        type="time"
                                        className={`form-control ${isEndTimeError ? 'is-invalid' : ''}`}
                                        style={{ background: '#3a3a3a' }}
                                        name="end_time"
                                        ref={endTimeInputRef}
                                        value={formData.end_time}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {!organization?.building && (
                        <div className="form-group boxed custom">
                            <AutocompleteAddress
                                label="Indirizzo servizio"
                                value={address}
                                setValue={setAddress}
                                handleRetrive={handleRetriveAddress}
                                handleReset={handleResetAddress}
                                defaultLockValue={!!addressObj}
                                required
                                withGoogle
                            />
                        </div>
                    )}

                    <div className="row">
                        <div className="col-6">
                            <ToggleField
                                id="service-enable-travel"
                                label="Abilita trasferta"
                                checked={toggleValues.travelEnabled}
                                onChange={() => handleToggleChange('travelEnabled')}
                            />
                        </div>
                        <div className="col-6">
                            <StaticField label="Costo trasferta">
                                <Form.Control
                                    type="text"
                                    value={travelCost}
                                    onChange={handleChangeTravelCost}
                                    disabled={!toggleValues.travelEnabled || isLoadingTravelCost}
                                />
                                {isLoadingTravelCost && (
                                    <div className="d-flex align-items-center gap-2 mt-2 text-white-50 small">
                                        <Spinner animation="border" size="sm" />
                                        <span>Calcolo costo trasferta...</span>
                                    </div>
                                )}
                            </StaticField>
                        </div>
                    </div>

                    {toggleValues.travelEnabled && (
                        <div className="form-group boxed custom">
                            <AutocompleteAddress
                                label="Indirizzo di partenza"
                                value={travelAddress}
                                setValue={setTravelAddress}
                                handleRetrive={handleRetriveTravelAddress}
                                handleReset={handleResetTravelAddress}
                                defaultLockValue={!!travelAddressObj}
                                required={toggleValues.travelEnabled}
                                withGoogle
                            />
                        </div>
                    )}

                    <StaticField label="Note">
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            placeholder="Aggiungi una nota"
                        />
                    </StaticField>

                    <Form.Label className="ms-1 mb-1 text-white">Servizi</Form.Label>

                    {serviceRows.map((serviceRow, index) => {
                        const selectedProduct = getProductById(serviceRow.productId);
                        const canFastDeliver = supportsFastDelivery(selectedProduct);
                        const fastDeliveryPrice = getFastDeliveryPrice(selectedProduct);
                        const isRemovable = index !== 0;

                        return (
                        <div className="row gx-2 align-items-center" key={index}>

                            <div className={canFastDeliver ? 'col-6' : 'col-8'}>
                                <StaticField label="Seleziona servizio">
                                    <Form.Select
                                        className="form-control"
                                        value={serviceRow.productId}
                                        required
                                        onChange={(event) => handleChangeSelectedProduct(index, event)}
                                    >
                                        <option value="">
                                            {isLoadingProducts ? 'Caricamento servizi...' : 'Seleziona servizio'}
                                        </option>
                                        {groupedProducts.map(group => (
                                            <optgroup key={group.type} label={group.label}>
                                                {group.items.map(product => (
                                                    <option key={product.id} value={product.id}>
                                                        {product.description}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </Form.Select>
                                </StaticField>
                            </div>

                            <div className="col-2">
                                <StaticField label="Prezzo">
                                    <Form.Control
                                        type="text"
                                        value={serviceRow.price}
                                        onChange={(event) => handleChangeServicePrice(index, event)}
                                    />
                                </StaticField>
                            </div>

                            {canFastDeliver && (
                                <div className="col-2">
                                    <StaticField label="24h">
                                        <div
                                            className="form-control d-flex align-items-center justify-content-center"
                                            style={{ height: '46px', backgroundColor: '#262626' }}
                                        >
                                            <FormCheck
                                                id={`service-enable-fast-delivery-${index}`}
                                                type="switch"
                                                label=""
                                                className="m-0"
                                                checked={!!serviceRow.fastDelivery}
                                                onChange={() => handleToggleServiceFastDelivery(index)}
                                            />
                                        </div>
                                    </StaticField>
                                </div>
                            )}

                            <div className="col-2">
                                <button
                                    type="button"
                                    style={{ height: '46px', visibility: isRemovable ? '' : 'hidden' }}
                                    className="btn btn-danger text-xl p-0 w-100 rounded-2"
                                    onClick={() => handleRemoveServiceRow(index)}
                                    aria-label="Rimuovi servizio"
                                    tabIndex={isRemovable ? 0 : -1}
                                >
                                    <IoTrash className='m-0' />
                                </button>
                            </div>

                            {/* {canFastDeliver && serviceRow.fastDelivery && (
                                <div className="col-12 text-white-50 small mb-2 ps-2">
                                    Consegna 24h: +{formatCurrencyValue(fastDeliveryPrice)} &euro;
                                </div>
                            )} */}

                        </div>
                        );
                    })}

                    <div className="form-group basic text-center mt-1 mb-2">
                        <button
                            type="button"
                            className="btn btn-success btn-icon btn-lg rounded-circle"
                            style={{ width: 48, height: 48 }}
                            onClick={handleAddServiceRow}
                        >
                            <IoAdd />
                        </button>
                    </div>

                    {fastDeliveryTotal > 0 && (
                        <StaticField label="Supplemento consegna 24h">
                            <Form.Control
                                type="text"
                                value={formatCurrencyValue(fastDeliveryTotal)}
                                readOnly
                                disabled
                            />
                        </StaticField>
                    )}

                    <StaticField label="Subtotale">
                        <Form.Control
                            type="text"
                            value={subtotal}
                            readOnly
                            disabled
                        />
                    </StaticField>

                    <div className="row">
                        <div className="col-4">
                            <ToggleField
                                id="service-enable-discount"
                                label="Abilita sconto"
                                checked={toggleValues.discountEnabled}
                                onChange={() => handleToggleChange('discountEnabled')}
                            />
                        </div>
                        <div className="col-4">
                            <StaticField label="Descrizione sconto">
                                <Form.Control
                                    type="text"
                                    value={discountDescription}
                                    onChange={(event) => setDiscountDescription(event.target.value)}
                                    disabled={!toggleValues.discountEnabled}
                                />
                            </StaticField>
                        </div>
                        <div className="col-4">
                            <StaticField label="Sconto">
                                <Form.Control
                                    type="text"
                                    value={discount}
                                    onChange={(event) => setDiscount(event.target.value)}
                                    disabled={!toggleValues.discountEnabled}
                                />
                            </StaticField>
                        </div>
                    </div>


                    <StaticField label="Totale Iva Esclusa">
                        <Form.Control
                            type="text"
                            value={totalVatExcluded}
                            disabled
                            readOnly
                        />
                    </StaticField>

                    {parseCurrencyInput(totalVatExcluded) < 0 && (
                        <div className="text-danger small justify-content-center d-flex gap-1">
                            Il totale è negativo — verifica prezzi e sconto.
                        </div>
                    )}

                    <div className="form-group basic">
                        <Button className="btn-block" type="submit" disabled={parseCurrencyInput(totalVatExcluded) < 0}>Conferma</Button>
                    </div>
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    </>
    );
}
